import { logout, useJwtToken, useProfile } from "@website/hooks/auth-manager";
import { MaterialIcon } from "@website/icons/material";
import { USER_TRACKING_EVENTS } from "@website/tracking/tracking.types";
import { addEventToTracking } from "@website/tracking/user";
import { Badge } from "@website/ui/badge";
import { ListItemSizeType } from "@website/ui/list-item";
import { isChannel } from "@website/utils/channel/helpers";
import { numberConvert } from "@website/utils/number-convert";
import { useSnappPro } from "@website/widget/header/hooks/use-snapp-pro";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";
import avatarB2B from "../../public/images/avatar-b2b.svg";
import avatar from "../../public/images/avatar.svg";
import { useGetBalance } from "../../services/profile/wallet";

export const useNavbar = () => {
  const { isLoading, data: profile } = useProfile();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const { profileType, role } = useJwtToken();

  const { hasSnappPro, snappProIcon, snappProTitle, snappProSubTitle } =
    useSnappPro();

  const {
    balance: { balance },
    isBalanceError,
    isLoadingGetBalance,
    isSuccess
  } = useGetBalance();
  const router = useRouter();

  const isB2BAdmin = useMemo(
    () =>
      role === "corporate.admin" || role === "agency.admin" || role === "admin",
    [role]
  );

  const hasWallet = useMemo(
    () => !isBalanceError && !isLoadingGetBalance && isSuccess,
    [isBalanceError, isLoadingGetBalance, isSuccess]
  );

  const onLogoutClick = useCallback(() => {
    logout();
    window.location.href = "/";
  }, []);

  const onClickHandler = useCallback(
    (pathname: string, evenetName: string) => {
      const product = router?.query?.product;
      if (product) {
        const source = router?.query?.source || "profile";
        window?.webengage?.track(evenetName, {
          product,
          "random-usernumber": profile?.phoneNumber?.slice(-1)
        });

        router?.push({
          pathname,
          query: {
            ...router.query,
            ...(source ? { source } : {}),
            ...(product ? { product } : {})
          }
        });
      } else {
        router?.push(pathname);
      }
    },
    [profile?.phoneNumber, router]
  );

  const onClickMyTripsHandler = useCallback(
    (pathname: string, evenetName: string) => {
      const source = router?.query?.source || "profile";
      window?.webengage?.track(evenetName, {
        source
      });

      router?.push({
        pathname,
        query: {
          ...router.query,
          ...(source ? { source } : {}),
          selected_service: "HOTEL"
        }
      });
    },
    [router]
  );

  const list = useMemo(() => {
    const handleClickOnWallet = () => {
      onClickHandler("/wallet", "st-wallet-clicked");
      addEventToTracking(USER_TRACKING_EVENTS.WALLET_CLICKED, {
        wallet_balance: balance,
        product: "core"
      });
    };

    const handleClickOnTransaction = () => {
      onClickHandler("/transactions", "st-transactions-clicked");
      addEventToTracking(USER_TRACKING_EVENTS.TRANSACTION_LIST_CLICKED, {
        wallet_balance: balance,
        product: "core"
      });
    };

    const walletMenu = {
      role: "button",
      title: (
        <section className="flex items-center justify-between">
          <span>کیف پول</span>
          <span className="text-caption text-on-surface-high-emphasis flex gap-1">
            <span dir="ltr">
              {balance
                ? numberConvert((balance / 10).toLocaleString(), {
                    locale: "fa"
                  })
                : 0}
            </span>
            <span>تومان</span>
          </span>
        </section>
      ),
      selected: router.pathname === "/wallet",
      StartIcon: (
        <MaterialIcon name="account_balance_wallet" variant="outlined" />
      ),
      onClick: handleClickOnWallet,
      id: "profile.wallet-button",
      hasNewLabel: false
    };

    const transactionsMenu = {
      role: "button",
      title: "لیست تراکنش ها",
      selected: router.pathname === "/transactions",
      StartIcon: <MaterialIcon name="history" variant="outlined" />,
      onClick: handleClickOnTransaction,
      id: "transactions-button",
      hasNewLabel: false
    };

    const debitNoteMenu = {
      role: "button",
      title: "اعلامیه بدهکار",
      selected: router.pathname === "/debit-note",
      StartIcon: (
        <MaterialIcon name="insert_chart_outlined" variant="outlined" />
      ),
      onClick: () => router?.push("/debit-note"),
      id: "debit-note-button",
      hasNewLabel: false
    };

    const userManagementMenu = {
      role: "button",
      title: "مدیریت کاربران",
      selected: router.pathname === "/user-management",
      StartIcon: (
        <MaterialIcon name="perm_contact_calendar" variant="outlined" />
      ),
      onClick: () => router?.push("/user-management"),
      id: "user-management-button",
      hasNewLabel: false
    };

    // const tripManagementMenu = {
    //   role: "button",
    //   title: "مدیریت سفرها ",
    //   selected: router.pathname === "/trip-management",
    //   StartIcon: <MaterialIcon name="luggage" variant="outlined" />,
    //   onClick: () => router?.push("/trip-management"),
    //   id: "trip-management-button"
    // };

    const myTripsMenu = {
      role: "button",
      title: "سفرهای من",
      selected: router.pathname === "/my-trips",
      StartIcon: <MaterialIcon name="luggage" variant="outlined" />,
      onClick: () => {
        onClickMyTripsHandler("/my-trips", "mytrips-clicked");
      },
      id: "my-trips-button",
      hasNewLabel: false
    };

    const profileMenu =
      profileType === "B2C"
        ? {
            role: "button",
            title: (
              <div className="text-xs font-medium">
                {profile?.fullname || "نام کاربری"}
              </div>
            ),
            subtitle: (
              <div style={{ direction: "ltr", textAlign: "right" }}>
                {numberConvert(profile?.phoneNumber, { locale: "fa" })}
              </div>
            ),
            image: avatar.src,
            selected: router.pathname === "/information",
            size: "x-large" as ListItemSizeType,
            onClick: () =>
              onClickHandler("/information", "st-user-info-clicked"),
            id: "information-button",
            hasNewLabel: false
          }
        : {
            role: "button",
            title: (
              <div className="text-xs font-medium">
                {profile?.fullname || "نام کاربری"}
              </div>
            ),
            overline: (
              <div className="text-secondary text-right text-[10px]">
                {profile && "company" in profile && profile?.company?.name}
              </div>
            ),
            subtitle: (
              <div style={{ direction: "ltr", textAlign: "right" }}>
                {numberConvert(profile?.phoneNumber, { locale: "fa" })}
              </div>
            ),
            image: avatarB2B.src,
            selected: router.pathname === "/information",
            size: "x-large" as ListItemSizeType,
            onClick: () => router?.push("/information"),
            id: "information-button",
            hasNewLabel: false
          };

    return [
      profileMenu,
      ...(hasSnappPro
        ? [
            {
              title: (
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    {snappProTitle}
                    <div className="text-extra-caption text-on-surface-medium-emphasis">
                      {snappProSubTitle}
                    </div>
                  </div>
                  <Badge content="فعال" color="success" />
                </div>
              ),
              StartIcon: <img src={snappProIcon} alt="snapp-pro" />
            }
          ]
        : []),
      hasWallet ? walletMenu : {},
      {
        role: "button",
        title: "لیست مسافران",
        selected: router.pathname === "/passengers-list",
        StartIcon: <MaterialIcon name="people" variant="outlined" />,
        onClick: () =>
          onClickHandler("/passengers-list", "st-passengers-list-clicked"),
        id: "passengers-list-button",
        hasNewLabel: false
      },
      isB2BAdmin ? userManagementMenu : {},
      myTripsMenu,
      {
        role: "button",
        title: "خرید‌های خودکار",
        selected: router.pathname === "/auto-reserve",
        StartIcon: <MaterialIcon name="access_time" />,
        onClick: () => onClickHandler("/auto-reserve", ""),
        id: "auto-reserve-button",
        hasNewLabel: true
      },
      // isB2BAdmin ? tripManagementMenu : {},
      hasWallet ? transactionsMenu : {},
      isB2BAdmin ? debitNoteMenu : {},
      {
        role: "link",
        href: "tel:+982192000123",
        title: "پشتیبانی",
        StartIcon: <MaterialIcon name="support_agent" />
      },
      isChannel(["jek"])
        ? {}
        : {
            role: "button",
            onClick: onLogoutClick,
            title: "خروج از حساب",
            StartIcon: <MaterialIcon name="logout" />
          }
    ];
  }, [
    balance,
    router,
    profileType,
    profile,
    hasWallet,
    isB2BAdmin,
    onLogoutClick,
    onClickHandler,
    onClickMyTripsHandler
  ]);

  return {
    list,
    isLoading,
    isCollapsed,
    setIsCollapsed,
    profileType
  };
};
